/**
 * Commercial compensation (1.71, decision of 14/10/2026, «modCompensazione»
 * COMMERCIALE of the Excel): an issued invoice and a received invoice of the
 * same subject close each other for the same amount, VAT included, with no
 * bank movement; both settlements go on the virtual account «Compensazioni».
 * Three cases: they close in full, or the larger one stays open for the
 * difference, to be collected or paid through the bank.
 */
import { z } from 'zod';
import { formatMoney, fromCents, toCents } from './money.js';
import { idSchema, isoDateSchema, optionalText } from './validation.js';
/** The code of the virtual account «Compensazioni» in the table Conti (seeded with the Excel's code). */
export const COMPENSATION_ACCOUNT_CODE = '6';
const positiveMoney = z
    .string()
    .regex(/^\d{1,12}(\.\d{1,2})?$/, { message: 'Importo non valido' })
    .transform((value) => fromCents(toCents(value)))
    .refine((value) => toCents(value) > 0, { message: 'Importo non valido' });
/** The invoice of the other cycle it is compensated with, the date and the amount (positive). */
export const compensationSchema = z.object({
    otherId: idSchema,
    date: isoDateSchema,
    amount: positiveMoney,
    note: optionalText(200),
});
/** What the larger of the two residuals allows: the smaller one, so that one of them closes. */
export function compensationProposal(residual, otherResidual) {
    return fromCents(Math.min(Math.abs(toCents(residual)), Math.abs(toCents(otherResidual))));
}
export function compensationOutcome(issuedResidual, receivedResidual, amount) {
    const issued = Math.abs(toCents(issuedResidual)) - toCents(amount);
    const received = Math.abs(toCents(receivedResidual)) - toCents(amount);
    const kind = issued === 0 && received === 0
        ? 'totale'
        : received === 0
            ? 'resta-attiva'
            : issued === 0
                ? 'resta-passiva'
                : 'restano-entrambe';
    return { kind, issuedLeft: fromCents(issued), receivedLeft: fromCents(received) };
}
/** What is wrong with the amount, or null: no more than what is left on either invoice. */
export function compensationError(sides, amount) {
    if (toCents(amount) <= 0)
        return 'Importo non valido';
    for (const side of sides) {
        const left = Math.abs(toCents(side.residual));
        if (toCents(amount) > left) {
            return `Sulla fattura ${side.number} restano solo ${formatMoney(fromCents(left))}`;
        }
    }
    return null;
}
//# sourceMappingURL=compensations.js.map